import { getCurrentAccount } from "@mirage/service-auth";
import { loadAssistKVDataAPI } from "@mirage/service-compose/service/apiDataLoader";
import { callApiV2 } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import { AccessType } from "@mirage/shared/compose/assist-api";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { convertLinkMetadataToChatSources } from "../utils/convertLinkMetadataToChatSources";

import type { context_engine } from "@dropbox/api-v2-client";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { ItemDataParams } from "@mirage/service-compose/service/apiDataLoader";
import type { GuaranteedKVStorage } from "@mirage/storage";
import type { AgentId, WorkflowAgent, WorkflowValues } from "../types";

// This file contains implementation of the Conversations service specific to managing workflow
// agents.

const logger = tagged("conversation-service/workflowAgentsService");

export enum StorageKey {
  WorkflowAgents = "workflow-agents",
}

export type WorkflowAgentsStorage = {
  agents: { [key: string]: WorkflowAgent };
  sources: { [key: string]: ChatSource };
};

export function createWorkflowAgentsService(
  storage: GuaranteedKVStorage<WorkflowAgentsStorage>,
) {
  let workflowAgentsListApiInProgress:
    | Promise<[WorkflowAgent[], WorkflowAgent[]]>
    | undefined;

  /**
   * Get workflow values for a specific agent.
   * @param agentId The ID of the agent to retrieve workflow values for.
   * @returns The workflow values for the specified agent, or an empty object if none exist.
   */
  async function getWorkflowAgent(agentId: string): Promise<WorkflowAgent> {
    const agents = await storage.get("agents");
    return agents[agentId] ?? {};
  }

  /**
   * Set workflow values for a specific agent.
   * @param agentId The ID of the agent to set workflow values for.
   * @param values The workflow values to set for the agent.
   */
  async function setWorkflowAgent(
    agentId: string,
    values: WorkflowValues,
    sourceContents?: ChatSource[],
  ): Promise<WorkflowAgent> {
    const agents = await storage.get("agents");
    const currentAccount = await getCurrentAccount();
    const existingAgent = agents[agentId];
    agents[agentId] = {
      dataId: existingAgent?.dataId,
      id: agentId,
      icon: (values[5].icon ?? "") as string,
      theme: (values[5].theme ?? "0") as string,
      name: (values[5].name ?? "") as string,
      description: (values[5].description ?? "") as string,
      model: (values[2]["base-model"] ?? "") as string,
      type: (values[1].type ?? "conversation") as AgentId,
      sources: (values[4].sources ?? []) as string[],
      sourceContents: sourceContents ?? [],
      allowCustomSources: (values[4]["custom-sources"] ?? false) as boolean,
      workflow: values,
      createdBy: {
        display_name: currentAccount?.name.display_name ?? "Unknown",
        profile_photo_url: currentAccount?.profile_photo_url,
        email: currentAccount?.email ?? "unknown",
      },
      createdAtMs: existingAgent?.createdAtMs ?? Date.now(),
      updatedAtMs: Date.now(),
      accessType: (values[5].publish ?? "private") as "private" | "company",
    };
    await storage.set("agents", agents);
    logger.debug(`Updated workflow values for agent: ${agentId}`);
    return agents[agentId];
  }

  async function setWorkflowAgentDataId(agentId: string, dataId: string) {
    const agents = await storage.get("agents");
    agents[agentId].dataId = dataId;
    await storage.set("agents", agents);
  }

  async function saveWorkflowAgent(agent: WorkflowAgent) {
    const args: context_engine.AssistSaveUserDataArg = {
      data_item: {
        user_data_type: StorageKey.WorkflowAgents,
        user_data_key: agent.id,
        user_data: JSON.stringify(agent),
      },
      data_id: agent.dataId,
      access_type: {
        ".tag": agent.accessType === "private" ? "individual" : "team",
      },
    };
    logger.log("Saving workflow agent to API...", agent.id);
    const response = await callApiV2(
      "contextEngineAssistApiSaveUserData",
      args,
    );
    if (response.data_id) {
      agent.dataId = response.data_id;
      await setWorkflowAgentDataId(agent.id, agent.dataId);
      // Update the full agent data in storage
      const agents = await storage.get("agents");
      agents[agent.id] = agent;
      await storage.set("agents", agents);
    }
    logger.log("saved workflow agent", agent.id);
    return agent;
  }

  async function removeWorkflowAgentLocal(agentId: string) {
    const agents = await storage.get("agents");
    delete agents[agentId];
    await storage.set("agents", agents);
  }

  async function deleteWorkflowAgent(agent: WorkflowAgent) {
    const args: context_engine.AssistDeleteUserDataArg = {
      data_id: agent.dataId,
    };
    await callApiV2("contextEngineAssistApiDeleteUserData", args);

    const agents = await storage.get("agents");
    delete agents[agent.id];
    await storage.set("agents", agents);

    logger.log("deleted workflow agent", agent.id);

    return;
  }

  async function getWorkflowAgents(): Promise<WorkflowAgent[]> {
    const agents = await storage.get("agents");
    const agentsArray = Object.values(agents);
    if (agentsArray.length === 0) {
      await loadApiWorkflowAgents();
      return getWorkflowAgents();
    }
    return agentsArray;
  }

  async function getChatSourcesByIds(ids: string[]): Promise<ChatSource[]> {
    const sources = (await storage.get("sources")) || {};
    const existingSources = ids
      .filter((id) => sources[id])
      .map((id) => sources[id]);

    const newSourcesUuids = ids.filter((id) => !sources[id]);
    let newSources: ChatSource[] = [];

    if (newSourcesUuids.length > 0) {
      try {
        const linkMetadataResponse = await callApiV2("dcsGetLinkMetadata", {
          url_or_uuids: newSourcesUuids.map((uuid) => ({
            link_type: {
              ".tag": "uuid",
              uuid,
            },
          })),
          include_body: true,
        });

        newSources = convertLinkMetadataToChatSources(
          linkMetadataResponse.results,
        );

        for (const source of newSources) {
          const uuid = getSourceUUID(source);
          if (uuid) {
            sources[uuid] = source;
          }
          source.inclusionType = "scope";
        }

        await storage.set("sources", sources);
      } catch (e) {
        logger.error(
          "failed to fetch and set newSources from dcsGetLinkMetadata",
          e,
        );
        return existingSources;
      }
    }

    return [...existingSources, ...newSources];
  }

  async function loadApiWorkflowAgents(): Promise<WorkflowAgent[]> {
    if (!workflowAgentsListApiInProgress) {
      workflowAgentsListApiInProgress = Promise.all([
        loadAssistKVDataAPI<WorkflowAgent>(
          StorageKey.WorkflowAgents,
          parseWorkflowAgentData,
        ),
        loadAssistKVDataAPI<WorkflowAgent>(
          StorageKey.WorkflowAgents,
          parseWorkflowAgentData,
          AccessType.TEAM,
        ),
      ]);
      const [userAgents, teamAgents] = await workflowAgentsListApiInProgress;
      const agents = [...userAgents, ...teamAgents];

      // Convert array to object with agent.id as keys
      const agentsMap = agents.reduce(
        (acc, agent) => {
          if (agent) {
            acc[agent.id] = agent;
          }
          return acc;
        },
        {} as Record<string, WorkflowAgent>,
      );
      await storage.set("agents", agentsMap);
      workflowAgentsListApiInProgress = undefined;
      return agents;
    }

    // If API call is already in progress, wait for it to complete and return flattened agents
    const [userAgents, teamAgents] = await workflowAgentsListApiInProgress;
    return [...userAgents, ...teamAgents];
  }

  function parseWorkflowAgentData(
    itemData: ItemDataParams,
  ): WorkflowAgent | undefined {
    try {
      const agent: WorkflowAgent = {
        ...JSON.parse(itemData.data),
        dataId: itemData.key,
      };
      return agent;
    } catch (error) {
      logger.error("Failed to parse workflow agent data:", error);
      return undefined;
    }
  }

  return {
    getWorkflowAgent,
    setWorkflowAgent,
    getWorkflowAgents,
    saveWorkflowAgent,
    deleteWorkflowAgent,
    loadApiWorkflowAgents,
    getChatSourcesByIds,
    removeWorkflowAgentLocal,
  };
}
